import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ADMIN_UNAUTHORIZED_EVENT, api } from '../api';
import { getRuntimeConfig } from '../config/runtimeConfig';

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const location = useLocation();
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const runtimeConfig = getRuntimeConfig();
  const hasRuntimeAdminAccess = Boolean(runtimeConfig?.permissions?.isAdmin || runtimeConfig?.permissions?.canEdit);
  const isSignedIn = Boolean((runtimeConfig?.currentUser?.id ?? 0) > 0);
  const isAdminRoute = location.pathname === '/admin' || location.pathname.startsWith('/admin/');
  const shouldCheckAdminAuth = isAdminRoute || hasRuntimeAdminAccess;

  const refreshAdmin = useCallback(async () => {
    if (!shouldCheckAdminAuth) {
      setAdminUser(null);
      setLoading(false);
      return null;
    }

    try {
      const user = await api.getAdminMe();
      setAdminUser(user);
      return user;
    } catch {
      setAdminUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [shouldCheckAdminAuth]);

  useEffect(() => {
    if (!shouldCheckAdminAuth) {
      setAdminUser(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    refreshAdmin();
  }, [refreshAdmin, shouldCheckAdminAuth]);

  useEffect(() => {
    if (!shouldCheckAdminAuth) {
      return undefined;
    }

    const syncAuthState = () => {
      refreshAdmin().catch(() => {});
    };

    const interval = setInterval(syncAuthState, 30000);
    window.addEventListener('focus', syncAuthState);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', syncAuthState);
    };
  }, [refreshAdmin, shouldCheckAdminAuth]);

  useEffect(() => {
    const handleUnauthorized = () => {
      setAdminUser(null);
      setLoading(false);
    };

    window.addEventListener(ADMIN_UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => window.removeEventListener(ADMIN_UNAUTHORIZED_EVENT, handleUnauthorized);
  }, []);

  const hasAdminAccess = hasRuntimeAdminAccess || Boolean(adminUser);

  const login = useCallback(async (username, password) => {
    await api.adminLogin(username, password);
    await refreshAdmin();
  }, [refreshAdmin]);

  const value = useMemo(() => ({
    adminUser,
    loading,
    login,
    refreshAdmin,
    isSignedIn,
    hasRuntimeAdminAccess,
    hasAdminAccess,
    isAuthenticated: hasAdminAccess
  }), [adminUser, loading, login, refreshAdmin, isSignedIn, hasRuntimeAdminAccess, hasAdminAccess]);
  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) throw new Error('useAdminAuth must be used inside AdminAuthProvider');
  return context;
}
